/**
 * Agent URL and gateway token storage, parameterized so each host can
 * preserve its existing localStorage key shape.
 *
 *  - Paradex (prefix-style):  `paradex_ai_agent_<scope>:<addr>`
 *  - Paradigm (suffix-style): `<userId>:paradigm_ai_agent_<scope>`
 *
 * Hosts call `createAgentUrlStore({ keyFor, fallbackUrl, legacyKeyFor? })`
 * with their own key strategy. Existing user data continues to load.
 */

export type AgentUrlScope =
  | 'agentUrl'
  | 'agentToken'
  | 'deviceToken'
  | 'selectedModel';

/** Minimal storage backend contract (the `Storage` subset the store uses). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Volatile in-memory backend for `createAgentUrlStore`. Used by hosts whose
 * connection grant is server-resolved per session and must never touch
 * `localStorage` (e.g. the standalone's cloud-auth flow) — everything is
 * gone on reload, by design.
 */
export function createMemoryStorage(): StorageLike {
  const entries = new Map<string, string>();
  return {
    getItem: (key) => entries.get(key) ?? null,
    setItem: (key, value) => {
      entries.set(key, value);
    },
    removeItem: (key) => {
      entries.delete(key);
    },
  };
}

/**
 * The store's default backend: `localStorage`, touched only at call time
 * (inside the accessors' try/catch) so environments without it degrade to
 * no-ops instead of throwing at construction. Exported for hosts that need
 * to route a single scope back to persistent storage while the rest of the
 * store is memory-backed (see `AgentUrlStoreConfig.modelStorage`).
 */
export function createLocalStorage(): StorageLike {
  return {
    getItem: (key) => localStorage.getItem(key),
    setItem: (key, value) => {
      localStorage.setItem(key, value);
    },
    removeItem: (key) => {
      localStorage.removeItem(key);
    },
  };
}

export interface AgentUrlStoreConfig {
  /**
   * Compute the localStorage key for a given (scope, accountId) pair.
   * The accountId is whatever the host uses to identify the user
   * (Paradex address, Paradigm userId, etc.).
   */
  keyFor(scope: AgentUrlScope, accountId: string): string;

  /**
   * Build-time fallback URL — used by `getAgentUrl` when localStorage
   * has nothing. Paradex passes `AI_AGENT_URL`; Desktop passes the
   * same or omits.
   */
  fallbackUrl?: string;

  /**
   * Optional legacy-key strategy for one-time migration. When present,
   * `getAgentUrl`/`getAgentToken`/`getDeviceToken` first check the new
   * key, then fall back to the legacy key and migrate in-place.
   * Used by Desktop to absorb the historical `<scope>:<userId>` shape.
   */
  legacyKeyFor?(scope: AgentUrlScope, accountId: string): string;

  /**
   * Storage backend. Defaults to `localStorage`. Pass `createMemoryStorage()`
   * to keep the gateway URL + tokens out of persistent storage entirely.
   */
  storage?: StorageLike;

  /**
   * Backend for the `selectedModel` scope only. Defaults to `storage`.
   * The model pick is a user preference, not a connection secret, so hosts
   * that run the store on `createMemoryStorage()` (ephemeral connection
   * grants) pass `createLocalStorage()` here to keep the pick across
   * reloads without persisting the URL or tokens.
   */
  modelStorage?: StorageLike;
}

export interface AgentUrlStore {
  getAgentUrl(accountId: string): string | null;
  setAgentUrl(accountId: string, url: string): void;
  clearAgentUrl(accountId: string): void;

  getAgentToken(accountId: string): string | null;
  setAgentToken(accountId: string, token: string): void;
  clearAgentToken(accountId: string): void;

  getDeviceToken(accountId: string): string | null;
  setDeviceToken(accountId: string, token: string): void;
  clearDeviceToken(accountId: string): void;

  /**
   * Clear both pairing and device tokens for an account. Does NOT clear
   * the URL — callers decide whether to rewrite or remove it.
   */
  clearAgentSession(accountId: string): void;

  getSelectedModel(accountId: string): string | null;
  setSelectedModel(accountId: string, modelId: string): void;
}

function readWithMigration(
  storage: StorageLike,
  newKey: string,
  legacyKey: string | null,
): string | null {
  try {
    const current = storage.getItem(newKey);
    if (current != null) return current;
    if (legacyKey == null) return null;
    const legacy = storage.getItem(legacyKey);
    if (legacy == null) return null;
    try {
      storage.setItem(newKey, legacy);
      storage.removeItem(legacyKey);
    } catch {
      // best-effort migration
    }
    return legacy;
  } catch {
    return null;
  }
}

function writeWithLegacyCleanup(
  storage: StorageLike,
  newKey: string,
  legacyKey: string | null,
  value: string,
): void {
  try {
    if (value.length === 0) {
      storage.removeItem(newKey);
    } else {
      storage.setItem(newKey, value);
    }
    if (legacyKey != null) {
      storage.removeItem(legacyKey);
    }
  } catch {
    // storage unavailable
  }
}

function removeWithLegacyCleanup(
  storage: StorageLike,
  newKey: string,
  legacyKey: string | null,
): void {
  try {
    storage.removeItem(newKey);
    if (legacyKey != null) storage.removeItem(legacyKey);
  } catch {
    // storage unavailable
  }
}

export function createAgentUrlStore(
  config: AgentUrlStoreConfig,
): AgentUrlStore {
  const { keyFor, fallbackUrl, legacyKeyFor } = config;
  const storage: StorageLike = config.storage ?? createLocalStorage();
  const modelStorage: StorageLike = config.modelStorage ?? storage;
  const legacy = (scope: AgentUrlScope, accountId: string): string | null =>
    legacyKeyFor ? legacyKeyFor(scope, accountId) : null;

  return {
    getAgentUrl(accountId: string): string | null {
      const stored = readWithMigration(
        storage,
        keyFor('agentUrl', accountId),
        legacy('agentUrl', accountId),
      );
      if (stored != null && stored.length > 0) return stored;
      if (fallbackUrl != null && fallbackUrl.length > 0) return fallbackUrl;
      return null;
    },
    setAgentUrl(accountId: string, url: string): void {
      writeWithLegacyCleanup(
        storage,
        keyFor('agentUrl', accountId),
        legacy('agentUrl', accountId),
        url,
      );
    },
    clearAgentUrl(accountId: string): void {
      removeWithLegacyCleanup(
        storage,
        keyFor('agentUrl', accountId),
        legacy('agentUrl', accountId),
      );
    },

    getAgentToken(accountId: string): string | null {
      const stored = readWithMigration(
        storage,
        keyFor('agentToken', accountId),
        legacy('agentToken', accountId),
      );
      return stored != null && stored.length > 0 ? stored : null;
    },
    setAgentToken(accountId: string, token: string): void {
      writeWithLegacyCleanup(
        storage,
        keyFor('agentToken', accountId),
        legacy('agentToken', accountId),
        token,
      );
    },
    clearAgentToken(accountId: string): void {
      removeWithLegacyCleanup(
        storage,
        keyFor('agentToken', accountId),
        legacy('agentToken', accountId),
      );
    },

    getDeviceToken(accountId: string): string | null {
      const stored = readWithMigration(
        storage,
        keyFor('deviceToken', accountId),
        legacy('deviceToken', accountId),
      );
      return stored != null && stored.length > 0 ? stored : null;
    },
    setDeviceToken(accountId: string, token: string): void {
      writeWithLegacyCleanup(
        storage,
        keyFor('deviceToken', accountId),
        legacy('deviceToken', accountId),
        token,
      );
    },
    clearDeviceToken(accountId: string): void {
      removeWithLegacyCleanup(
        storage,
        keyFor('deviceToken', accountId),
        legacy('deviceToken', accountId),
      );
    },

    clearAgentSession(accountId: string): void {
      this.clearAgentToken(accountId);
      this.clearDeviceToken(accountId);
    },

    getSelectedModel(accountId: string): string | null {
      try {
        const stored = modelStorage.getItem(keyFor('selectedModel', accountId));
        return stored != null && stored.length > 0 ? stored : null;
      } catch {
        return null;
      }
    },
    setSelectedModel(accountId: string, modelId: string): void {
      try {
        if (modelId.length === 0) {
          modelStorage.removeItem(keyFor('selectedModel', accountId));
        } else {
          modelStorage.setItem(keyFor('selectedModel', accountId), modelId);
        }
      } catch {
        // storage unavailable
      }
    },
  };
}

/**
 * Derive the dashboard URL from a WebSocket agent URL, preserving the port and
 * mapping the scheme: `ws://host:port` → `http://host:port/overview`,
 * `wss://host[:port]` → `https://host[:port]/overview`.
 */
export function deriveAgentDashboardUrl(agentWsUrl: string): string | null {
  try {
    const parsed = new URL(agentWsUrl);
    const scheme =
      parsed.protocol === 'ws:' || parsed.protocol === 'http:'
        ? 'http:'
        : 'https:';
    // `host` keeps the port (unlike `hostname`).
    return `${scheme}//${parsed.host}/overview`;
  } catch {
    return null;
  }
}

/**
 * Validate that a URL is a usable OpenClaw gateway WebSocket URL: must be
 * `ws://` or `wss://` and must include a host. Mirrors paradex's
 * `ValidateAiAgentUrl` (paradex web-api account validation).
 */
export function isValidAgentUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'ws:' && parsed.protocol !== 'wss:') return false;
    if (parsed.hostname.length === 0) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether a URL is safe to load as an embedded third-party app frame.
 * App manifests arrive over the gateway WebSocket (`app.register`) as
 * untrusted input, and the URL is fed directly to an iframe `src`. Only
 * absolute http(s) URLs are allowed — this rejects `javascript:`, `data:`,
 * `vbscript:`, `blob:` and other script-bearing or origin-ambiguous schemes
 * that would otherwise execute in (or alongside) the trader's session.
 */
export function isSafeAppUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' || parsed.protocol === 'http:';
}
